import React, { useEffect, useState } from 'react';
import { Box, Preloader, Text } from '@common-fe/common-fe';

import { DocumentPagePreview } from '@/components/elements/DropScan/dropScan.types';
import useConvertPdfToPng from '@/utils/hooks/useConvertPdfToPng';

import { useFileConvertToLMNStore } from '../../stores';
import { DocumentPreviewImage as RoundedImg } from '../DocumentPreviewImage';

import PdfPasswordPrompt from './PdfPasswordPrompt';

interface Props {
  url?: string;
  alt?: string;
  isConvertMode?: boolean;
  onSetPreviews?: (previews: DocumentPagePreview[]) => void;
  isFixedDocumentSize?: boolean;
  allowPasswordEntry?: boolean;
}

const PdfScanViewer: React.FC<Props> = ({
  isConvertMode, url, alt, onSetPreviews, isFixedDocumentSize, allowPasswordEntry,
}) => {
  const selectedIds = useFileConvertToLMNStore((state) => state.selectIndexes);
  const { pngDocuments, isLoading, isPasswordProtected, isPasswordIncorrect, submitPassword } =
    useConvertPdfToPng({ url });
  const [previews, setPreviews] = useState<DocumentPagePreview[]>([]);

  useEffect(() => {
    if (pngDocuments?.length) {
      let newPreviews = pngDocuments.map(
        (pngDocument): DocumentPagePreview => ({
          ...pngDocument,
          pageRef: React.createRef<HTMLDivElement>(),
        })
      );

      setPreviews(newPreviews);

      if (onSetPreviews) {
        if (isConvertMode) {
          newPreviews = newPreviews.filter((item, index) => selectedIds.includes(index));
        }
        onSetPreviews(newPreviews);
      }
    }
  }, [pngDocuments, onSetPreviews, selectedIds, isConvertMode]);

  if (isLoading) {
    return (
      <Box height="60px" justify="center" align="center">
        <Preloader testId="pdf-document-preloader" />
      </Box>
    );
  }

  if (isPasswordProtected) {
    return (
      <Box
        height="453px"
        justify="center"
        align="center"
        style={{ marginLeft: '-60px' }}
      >
        {allowPasswordEntry ? (
          <PdfPasswordPrompt isIncorrect={!!isPasswordIncorrect} onSubmit={submitPassword} />
        ) : (
          <Text data-testid="pdf-document-password-protected" color="textSecondary">
            This document is password protected.
          </Text>
        )}
      </Box>
    );
  }

  if (isConvertMode) {
    return (
      <Box height={{ min: 'auto' }}>
        {previews.map((pngDocument, index) => (
          <Box
            key={pngDocument?.id}
            ref={pngDocument.pageRef}
            margin={{ top: 'spacing12' }}
            style={{ display: selectedIds.includes(index) ? 'block' : 'none' }}
          >
            <RoundedImg src={pngDocument?.link} alt={alt} $isFixedSize={isFixedDocumentSize} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box height={{ min: 'auto' }}>
      {previews.map((pngDocument) => (
        <Box key={pngDocument?.id} ref={pngDocument.pageRef} margin={{ top: 'spacing12' }}>
          <RoundedImg src={pngDocument?.link} alt={alt} $isFixedSize={isFixedDocumentSize} />
        </Box>
      ))}
    </Box>
  );
};

export default PdfScanViewer;
